import { FC } from 'react';
import { useRouter } from 'next/router';
import { logEvent } from '@/analytics';
import type { IFundTransaction } from '@/services/apis/fund/schemas';
import { useAPI } from '@/services/index';
import { TransactionCard } from '@/modules/invest/subModules/fund/components/TransactionCard';
import { useGetFundCodeFromQuery } from '@/modules/invest/hooks/useGetFundCodeFromQuery';
import { useSetData } from '@/data/Data.hook';
import { getFundTransactionStatusVariant } from '@/modules/invest/utils/getFundTransactionStatusVariant';
import { getFundTransactionTypeTitle } from './PendingFundTransactions.utils';
import { PendingTransactionsWrapper } from '../PendingTransactionsWrapper';

const PENDING_TRANSACTIONS_PAGE_SIZE = 20;

export const PendingFundTransactions: FC = () => {
  const { push } = useRouter();

  const fundCode = useGetFundCodeFromQuery();

  const { data } = useAPI(['fund', 'getTransactions'], {
    input: {
      pageNo: 0,
      pageSize: PENDING_TRANSACTIONS_PAGE_SIZE,
      fundCode,
      status: ['Created', 'Pending', 'WaitForAccept'],
    },
  });

  const setFundIssuanceData = useSetData('fundTransaction');

  const handleTransactionClick =
    (transaction: IFundTransaction, pageIndex: number) => async () => {
      logEvent('FUNDS_TRANSACTIONS_PAGE_DETAILS', {
        name: transaction.fundCode,
      });
      setFundIssuanceData({ ...transaction, pageIndex });

      const transactionId = transaction.id || transaction.requestDate;

      await push({
        pathname: `/invest/fund/transaction/${transactionId}`,
      });
    };

  return (
    <PendingTransactionsWrapper transactionLength={data?.items.length}>
      {data?.items.map((transaction, index) => {
        const title = getFundTransactionTypeTitle(transaction.transactionType);

        const variant = getFundTransactionStatusVariant(transaction.status);

        return (
          <TransactionCard
            key={transaction.id}
            amount={transaction.approvedAmount || transaction.amount}
            chipTitle={transaction.statusTitle}
            chipVariant={variant}
            date={transaction.requestDate}
            title={title}
            type={transaction.transactionType}
            onClick={handleTransactionClick(transaction, index)}
          />
        );
      })}
    </PendingTransactionsWrapper>
  );
};
